import { CheckCircle2, AlertTriangle } from 'lucide-react';
import { BancoMCPReconciliationAccount } from '../../services/apiService';

interface Props {
  record: BancoMCPReconciliationAccount | null | undefined;
}

function formatBRL(value: string): string {
  const num = parseFloat(value);
  if (isNaN(num)) return value;
  return num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function isFresh(checkedAt: string | null): boolean {
  if (!checkedAt) return false;
  return Date.now() - new Date(checkedAt).getTime() < 24 * 60 * 60 * 1000;
}

export function SaldoMCPBadge({ record }: Props) {
  // State 1: no record or mcp_id missing
  if (!record || !record.mcp_id) return null;

  // State 4: never computed (ok or diff is null)
  if (record.ok === null || record.diff === null) {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
        não verificado
      </span>
    );
  }

  const fresh = isFresh(record.checked_at);

  // State 5: stale (checked_at > 24h or null)
  if (!fresh) {
    const tooltip = record.checked_at
      ? `Verificado em: ${new Date(record.checked_at).toLocaleString('pt-BR')}`
      : 'Nunca verificado';
    return (
      <span
        className="inline-flex items-center gap-1 text-xs font-medium text-gray-400 bg-gray-50 px-2 py-0.5 rounded-full border border-gray-200"
        title={tooltip}
      >
        <AlertTriangle className="h-3 w-3" /> desatualizado
      </span>
    );
  }

  // State 2: ok=true and fresh
  if (record.ok === true) {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
        <CheckCircle2 className="h-3 w-3" /> Saldo confere
      </span>
    );
  }

  // State 3: ok=false and fresh — show sign-aware diff
  return (
    <span className="inline-flex items-center gap-1 text-xs font-medium text-red-700 bg-red-100 px-2 py-0.5 rounded-full">
      <AlertTriangle className="h-3 w-3" /> Saldo diverge ({formatBRL(record.diff)})
    </span>
  );
}
